import { useState } from 'react';
import {
  IonInput,
  IonButton,
  IonChip,
  IonIcon,
  IonLabel,
  IonNote,
} from '@ionic/react';
import { location, close } from 'ionicons/icons';
import { deriveGeocode, nameToCoords, coordsToNearestPlace } from '@/lib/geo/places';
import type { DesiredLocation, GeocodePrecisionTier } from '@/types';

const RADIUS_OPTIONS = [5, 10, 25, 50];

export interface DesiredLocationEditorProps {
  value: DesiredLocation[];
  onChange: (next: DesiredLocation[]) => void;
  // When false, no commute radius control is shown (e.g. a single work location).
  radiusEnabled?: boolean;
  // Precision tier used to derive the geocode. Public = locality only.
  tier?: GeocodePrecisionTier;
}

export function DesiredLocationEditor({
  value,
  onChange,
  radiusEnabled = true,
  tier = 'Public',
}: DesiredLocationEditorProps) {
  const [text, setText] = useState('');
  const [radius, setRadius] = useState<number>(25);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addLocation = async (label: string, coords: { latitude: number; longitude: number }) => {
    const geo = deriveGeocode(coords, tier);
    const next: DesiredLocation = {
      label,
      plusCode: geo.plusCode,
      plusCodeShort: geo.plusCodeShort,
      h3Index: geo.h3Index,
      h3Resolution: geo.h3Resolution,
      geocodePrecision: geo.geocodePrecision,
      ...(radiusEnabled ? { radiusKm: radius } : {}),
    };
    onChange([...value.filter((d) => d.label !== label), next]);
    setText('');
    setError(null);
  };

  const handleAddTyped = async () => {
    const q = text.trim();
    if (!q) return;
    const place = await nameToCoords(q);
    if (!place) {
      setError(`No match for "${q}". Try a suburb or city.`);
      return;
    }
    await addLocation(place.name, { latitude: place.latitude, longitude: place.longitude });
  };

  const handleUseMyLocation = () => {
    if (!('geolocation' in navigator)) {
      setError('Location is unavailable on this device.');
      return;
    }
    setBusy(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        const place = await coordsToNearestPlace(coords.latitude, coords.longitude);
        const label = place?.name ?? 'My current area';
        await addLocation(label, coords);
        setBusy(false);
      },
      () => {
        setError('Could not detect your location.');
        setBusy(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
    );
  };

  const remove = (label: string) => onChange(value.filter((d) => d.label !== label));

  return (
    <div className="bp-loc-editor">
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <IonInput
          placeholder="Suburb or city"
          value={text}
          onIonInput={(e) => setText(e.detail.value!)}
        />
        <IonButton fill="outline" onClick={handleAddTyped} disabled={busy}>
          Add
        </IonButton>
        <IonButton fill="clear" onClick={handleUseMyLocation} disabled={busy}>
          <IonIcon icon={location} />
        </IonButton>
      </div>

      {radiusEnabled && (
        <div className="bp-loc-radius" style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          <IonLabel style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted)', alignSelf: 'center' }}>
            Commute:
          </IonLabel>
          {RADIUS_OPTIONS.map((r) => (
            <button
              key={r}
              type="button"
              className={`bp-loc-radius-btn ${radius === r ? 'active' : ''}`}
              onClick={() => setRadius(r)}
            >
              {r} km
            </button>
          ))}
        </div>
      )}

      {error && <IonNote color="danger">{error}</IonNote>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {value.map((loc) => (
          <IonChip key={loc.label} outline>
            <IonIcon icon={location} />
            {loc.label}
            {radiusEnabled && loc.radiusKm ? ` · within ${loc.radiusKm} km` : ''}
            <IonIcon icon={close} onClick={() => remove(loc.label)} />
          </IonChip>
        ))}
      </div>
    </div>
  );
}
